<template>
  <CustomLineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    class="make-a-copy"
    v-bind="$attrs"
    @reload="getData"
  >
    <v-row justify="center">
      <v-col
        cols="10"
        class="custom-line-chart-select"
      >
        <SelectStocks
          :stock="activeStock"
          :item-id="itemId"
          @change="toggle"
        />
      </v-col>
    </v-row>
  </CustomLineChart>
</template>

<script>
import CustomLineChart from '@/components/cards/models/CustomLineChart';
import SelectStocks from '@/components/cards/SelectStocks';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';
import colors from '@/utils/colors';
import moment from 'moment';
import { NORMALIZED_TIME_PATTERN } from '@/utils/date';
import { getMetrics } from '@/utils/stocks';
import { sortSeries } from '@/utils/sorting';

const {
  neutralColor,
  positiveColor,
  linkedinColor,
  forunsColor,
} = colors;

export default {
  name: 'StockVsSocialLineChart',
  components: {
    CustomLineChart,
    SelectStocks,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    metricToCompare: {
      type: String,
      required: true,
    },
    stock: {
      type: Object,
      default: () => {
        return { name: 'VALE3', symbol: 'VALE3.SA' };
      },
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      undefinedLabeL: this.$t('DataManagement.undefined'),
      rawSeries: [],
      activeStock: this.stock,
      selectLabel: this.$t('stocks.bovespa'),
      pointsParser: 'series',
      filteredValues: [],
      categories: [],
      cardOptions: {
        chart: {
          type: 'spline',
        },
        plotOptions: {
          column: {
            borderWidth: 0,
          },
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value: %d. %b}',
          },
        },
        yAxis: [{
          title: {
            text: this.metricToCompare,
          },
        },
        {
          title: {
            text: this.$t('components.StockVsSocialLineChart.label.mentionsText'),
          },
          opposite: true,
        },
        ],
        tooltip: {
          xDateFormat: '%d/%m/%Y',
        },
      },
    };
  },
  computed: {
    series() {
      if (!this.rawSeries && !this.rawSeries.length) {
        return [];
      }

      let series = this.rawSeries;

      if (this.filteredValues.length) {
        series = series?.filter(point => {
          return this.filteredValues.includes(point.name);
        }) || [];
      }

      const organizedSeries = series?.map(serie => {
        return {
          name: serie.name || this.undefinedLabeL,
          data: serie.data.map((val) => val || 0),
          yAxis: serie.yAxis || 0,
          type: serie.type || 'spline',
          color: serie.color || neutralColor,
          zIndex: serie.zIndex,
          marker: {
            enabled: false,
          },
        };
      });

      const sortedSeries = sortSeries(organizedSeries);
      return sortedSeries;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getWarroomService',
      'getIbovespaService',
    ]),
    orderDataArrayAsc(data) {
      return data.sort();
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      Promise.all([
        this.getWarroomService({
          itemId: this.itemId,
          servicePath: 'postInTime',
        }),
        this.getIbovespaService({
          servicePath: 'getStocksData',
          stockParams: {
            stockSymbol: this.activeStock.symbol,
          },
        }),
      ]).then(async (values) => {
        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }
        this.requestStatus = 'success';
        const THREE_HOURS_MILLIS = 10800000;
        const [warroomResponse, ibovespaResponse] = values;
        const categories = [];
        const socialData = [];
        const seriesData = warroomResponse.data[1];
        seriesData.map(([seriesDate, seriesValue]) => {
          categories.push(Number(moment(seriesDate, NORMALIZED_TIME_PATTERN).format('x')) - THREE_HOURS_MILLIS);
          socialData.push(seriesValue);
          return categories;
        });

        const metric = await getMetrics({ ibovespaResponse, categories, metricToCompare: this.metricToCompare });

        categories.forEach((category, index) => {
          categories[index] = category + THREE_HOURS_MILLIS;
        });

        this.categories = this.orderDataArrayAsc([...categories]);
        const series = [
          {
            'yAxis': 1,
            'name': this.$t('stocks.mentions'),
            'data': socialData.map((val, index) => [this.categories[index], val]),
            'color': linkedinColor,
            'zIndex': 1,
          },
          {
            'yAxis': 0,
            'name': this.translateMetric(this.metricToCompare),
            'data': metric.map((val, index) => [this.categories[index], val]),
            'type': this.metricToCompare === 'price' ? 'line' : 'column',
            'color': this.metricToCompare === 'price' ? forunsColor : positiveColor,
            'zIndex': 2,
          },
        ];
        this.rawSeries = series;
        if (this.metricToCompare === 'price') {
          this.cardOptions.yAxis[0].labels = {
            formatter() {
              return `R$${this.value.toLocaleString('pt-BR')}`;
            },
          };
          this.cardOptions.yAxis[0].title.text = this.$t('stocks.price');
        }
      });
    },
    translateMetric(metric) {
      const dict = {
        volume: this.$t('stocks.volume'),
        price: this.$t('stocks.price'),
      };
      return metric in dict ? dict[metric] : metric;
    },
    toggle(activeStock) {
      this.activeStock = activeStock;
      this.getData();
    },
  },
};
</script>

<style scoped lang="scss">
  .custom-line-chart-select {
    margin: auto;
    padding: 0;
  }

  ::v-deep {
    .line-chart-container {
      height: calc(100% - 40px) !important;
    }

    .line-chart {
      height: calc(100% - 40px) !important;
    }
  }
</style>
